/**
 * ObsTaggedNotesPanel — read-only note list with live Tag reassignment.
 *
 * Used by Criterion Mapping (per DSM / Other Behaviours page) and by the Task
 * Administration Self-Report page (to surface notes tagged `self_reported`
 * from tasks / ADOS while they still live in those source arrays).
 *
 * Cards stay taggable (`allowTagWhileReadOnly`) so clinicians can re-tag
 * without leaving the page — writes go back via `onUpdateNoteTags` + each
 * note's `sourceRef`. Untagging the protected page id asks the parent to
 * confirm, then the parent drives the exit animation via `exitingNoteIds` /
 * `exitingSnapshots`.
 */

import React, { useCallback, useMemo } from 'react';
import StructuredNotesEditor from './StructuredNotesEditor';

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {Array} props.notes - Tagged-note views (from collectObsTaggedNotes)
 * @param {string} props.emptyMessage
 * @param {string} props.pageId - Protected tag id (e.g. 'A1', 'self_reported')
 * @param {boolean} props.canEdit
 * @param {function} [props.onUpdateNoteTags] - (sourceRef, noteId, criteriaTags) => void
 * @param {Set|null} [props.exitingNoteIds]
 * @param {Array} [props.exitingSnapshots]
 * @param {function} [props.onRequestRemoveProtectedTag]
 * @param {string} [props.minHeight='12rem'] - Empty/body min height
 */
export default function ObsTaggedNotesPanel({
  title,
  subtitle,
  notes,
  emptyMessage,
  pageId,
  canEdit,
  onUpdateNoteTags,
  exitingNoteIds,
  exitingSnapshots,
  onRequestRemoveProtectedTag,
  minHeight = '12rem',
}) {
  // Merge live tagged notes with any snapshots still playing the exit
  // animation (those have already been removed from `notes` by the tag write).
  const displayNotes = useMemo(() => {
    const byId = new Map();
    for (const note of notes || []) {
      byId.set(note.id, note);
    }
    for (const snap of exitingSnapshots || []) {
      if (!byId.has(snap.id)) byId.set(snap.id, snap);
    }
    return Array.from(byId.values());
  }, [notes, exitingSnapshots]);

  // Group consecutive notes that share a source so we can show one caption
  // per source block rather than repeating it under every bullet.
  const groups = [];
  for (const note of displayNotes) {
    const last = groups[groups.length - 1];
    if (last && last.sourceLabel === note.sourceLabel) {
      last.notes.push(note);
    } else {
      groups.push({ sourceLabel: note.sourceLabel, notes: [note] });
    }
  }

  const handleUpdateTags = useCallback((noteId, criteriaTags) => {
    const note = displayNotes.find((n) => n.id === noteId);
    if (!note?.sourceRef || !onUpdateNoteTags) return;
    onUpdateNoteTags(note.sourceRef, noteId, criteriaTags);
  }, [displayNotes, onUpdateNoteTags]);

  const handleRequestRemoveProtected = useCallback((noteId, nextTags, protectedTagId) => {
    const note = displayNotes.find((n) => n.id === noteId);
    if (!note || !onRequestRemoveProtectedTag) return;
    onRequestRemoveProtectedTag({ note, nextTags, protectedTagId });
  }, [displayNotes, onRequestRemoveProtectedTag]);

  return (
    // overflow-visible so the Tag picker popover can extend past the card
    // chrome. Nested scrollports clip position:absolute menus no matter how
    // high their z-index is. Long lists scroll with the page.
    <div className="flex flex-col min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 rounded-t-xl">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div
        className="p-3 flex-1 overflow-visible"
        style={{ minHeight }}
      >
        {groups.length === 0 ? (
          <p className="text-sm text-gray-400 italic px-1 py-6 text-center">
            {emptyMessage}
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.sourceLabel || 'untitled'}>
                {group.sourceLabel && (
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 px-1 mb-1">
                    {group.sourceLabel}
                  </p>
                )}
                <StructuredNotesEditor
                  notes={group.notes}
                  onChange={undefined}
                  readOnly
                  readOnlyPlaceholder={emptyMessage}
                  minHeight="3rem"
                  domainId={`tagged-${pageId}-${group.sourceLabel || 'none'}`}
                  // Re-tag in place — writes back via onUpdateNoteTags so the
                  // source Task Admin / ADOS arrays stay in sync.
                  enableCriteriaTagging={canEdit}
                  allowTagWhileReadOnly={canEdit}
                  onUpdateNoteTags={canEdit ? handleUpdateTags : undefined}
                  protectedTagId={pageId}
                  onRequestRemoveProtectedTag={
                    canEdit ? handleRequestRemoveProtected : undefined
                  }
                  exitingNoteIds={exitingNoteIds}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
